import SubscriptionItem from 'UI/SubscriptionItem';
import { BonusSection } from 'UI/SubscriptionItem/components/BonusSection/BonusSection';
import { CourseTypeEnum, PaymentDtoTypeEnum, SubscribeDtoTypeEnum } from 'api/generated';
import arrowDow2 from 'assets/images/arrow-down2.png';
import arrowDown from 'assets/images/arrow-down.png';
import {
  bronzeCapabilities,
  bronzeCapabilitiesResponsive,
  commonCapabilities,
  goldCapabilities,
  goldCapabilitiesResponsive,
  levelGradeMapping,
  silverCapabilities,
  silverCapabilitiesResponsive,
} from 'constant';
import { useLottieJsonWithStatus } from 'hooks/useLottieJson';
import Lottie from 'lottie-react';
import React, { FC, useEffect, useState } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css } from 'styled-components';

import bogoAnimationSilver from '../../../../../assets/json/bogo/TNA_AllStar.json';
import bogoAnimationSilverDark from '../../../../../assets/json/bogo/TNA_AllStar_darkmode.json';
// BOGO tier animations
import bogoAnimationGold from '../../../../../assets/json/bogo/TNA_MVP.json';
import bogoAnimationGoldDark from '../../../../../assets/json/bogo/TNA_MVP_darkmode.json';
import bogoAnimationBronze from '../../../../../assets/json/bogo/TNA_Rookie.json';
import bogoAnimationBronzeDark from '../../../../../assets/json/bogo/TNA_Rookie_darkmode.json';
import { TrialText } from './components';
import {
  bronzeAnimatedMapping,
  bronzeDarkAnimatedMapping,
  goldAnimatedMapping,
  goldDarkAnimatedMapping,
  silverAnimatedMapping,
  silverDarkAnimatedMapping,
} from './constants';
import { ActivePlan, Plan, ProductsProps } from './types';

const Products: FC<ProductsProps> = ({
  monthlyPrices,
  annualPrices,
  purchasePrice,
  installmentPrice,
  currentPayment,
  mainColor,
  directionId,
  nextPayment,
  setIsShowBonusModal,
  courseId,
  // isCertificate,
  isTrialStarting,
  isSubscriptionUpdating,
  toggleSubscription,
  startUnsubscribeProcess,
  cancelSubscriptionDowngrade,
  startTrial,
  mostEffectiveLabel,
  _isBundle, // Prefixed with underscore since it's not used in subscription pricing
}) => {
  const dispatch = useAppDispatch();

  const isTrialText = currentPayment?.level === 'none' || nextPayment?.type === 'Free';
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePeriod, setActivePeriod] = useState('gold');
  const [planType, setPlanType] = useState<ActivePlan>();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isDarkMode = false;
  const courses = useAppSelector(selectors.courses.selectCourses);
  const selectedCourse = useAppSelector(selectors.courses.selectCourse);

  // Get course information to determine animation overrides
  const currentCourse = courses?.find((course) => course.id === courseId) || selectedCourse;
  const courseType = currentCourse?.type as string;

  const courseAny = currentCourse as any;
  const normalizeStr = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  const isResponsive = screenWidth <= 1180;
  const paymentStructureRaw = courseAny?.paymentStructure;
  const paymentStructure = typeof paymentStructureRaw === 'string' ? paymentStructureRaw.trim().toLowerCase() : '';
  const hasStudySubscriptions = Boolean(monthlyPrices) || Boolean(annualPrices);
  const isStudyMaterialCourse = courseType === 'default' || paymentStructure === 'study' || hasStudySubscriptions;
  const shouldCenterLegacy = isStudyMaterialCourse && !isResponsive;

  const goldUrl = normalizeStr(courseAny?.pricingAnimationGoldUrl);
  const silverUrl = normalizeStr(courseAny?.pricingAnimationSilverUrl);
  const bronzeUrl = normalizeStr(courseAny?.pricingAnimationBronzeUrl);
  const pointerUrl = normalizeStr(courseAny?.pricingAnimationPointerUrl);

  const { data: goldUrlData, status: goldUrlStatus } = useLottieJsonWithStatus(goldUrl);
  const { data: silverUrlData, status: silverUrlStatus } = useLottieJsonWithStatus(silverUrl);
  const { data: bronzeUrlData, status: bronzeUrlStatus } = useLottieJsonWithStatus(bronzeUrl);
  const { data: pointerUrlData, status: pointerUrlStatus } = useLottieJsonWithStatus(pointerUrl);

  const pointerLottieKey = pointerUrl
    ? `pricing:pointer:url:${pointerUrl}:${pointerUrlStatus}`
    : 'pricing:pointer:default';

  const getPlanAnimationKey = (planType: 'gold' | 'silver' | 'bronze') => {
    const url = planType === 'gold' ? goldUrl : planType === 'silver' ? silverUrl : bronzeUrl;
    const loaded =
      planType === 'gold'
        ? Boolean(goldUrlData)
        : planType === 'silver'
        ? Boolean(silverUrlData)
        : Boolean(bronzeUrlData);

    if (url) return loaded ? `pricing:${planType}:url:${url}:ready` : `pricing:${planType}:url:${url}:pending`;

    const rawOverrideKey =
      planType === 'gold'
        ? courseAny?.pricingAnimationGoldKey
        : planType === 'silver'
        ? courseAny?.pricingAnimationSilverKey
        : courseAny?.pricingAnimationBronzeKey;

    const overrideKey = normalizeStr(rawOverrideKey) || 'auto';
    return `pricing:${planType}:key:${overrideKey}:direction:${directionId}`;
  };

  // Function to determine if this is a bundle course (matching backend logic)
  const isBundleCourse = () => {
    // Backend uses: where: { type: CourseType.BUNDLE } where CourseType.BUNDLE = 'bundle'
    // Match the exact same logic here
    return courseType === 'bundle' || (currentCourse as any)?.type === 'bundle';
  };

  // Function to get animation data with course-specific overrides
  const getAnimationData = (planType: string, isDark = false) => {
    const hasUrl =
      planType === 'gold'
        ? Boolean(goldUrl)
        : planType === 'silver'
        ? Boolean(silverUrl)
        : planType === 'bronze'
        ? Boolean(bronzeUrl)
        : false;

    const urlStatus =
      planType === 'gold'
        ? goldUrlStatus
        : planType === 'silver'
        ? silverUrlStatus
        : planType === 'bronze'
        ? bronzeUrlStatus
        : 'idle';

    const urlData =
      planType === 'gold'
        ? goldUrlData
        : planType === 'silver'
        ? silverUrlData
        : planType === 'bronze'
        ? bronzeUrlData
        : null;

    // If a URL override is configured, do NOT show a fallback while loading.
    // This prevents the “default first, then override” flicker.
    if (hasUrl) {
      if (urlStatus === 'loaded' && urlData) return urlData;
      if (urlStatus === 'loading') return null;
      // if error, fall through to existing fallback logic
    }

    const rawOverrideKey =
      planType === 'gold'
        ? courseAny?.pricingAnimationGoldKey
        : planType === 'silver'
        ? courseAny?.pricingAnimationSilverKey
        : planType === 'bronze'
        ? courseAny?.pricingAnimationBronzeKey
        : undefined;
    const overrideKey = typeof rawOverrideKey === 'string' ? rawOverrideKey.trim() : '';

    if (overrideKey && overrideKey !== 'auto') {
      switch (overrideKey) {
        case 'bogo': {
          switch (planType) {
            case 'gold':
              return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
            case 'silver':
              return isDark ? bogoAnimationSilverDark : bogoAnimationSilver;
            case 'bronze':
              return isDark ? bogoAnimationBronzeDark : bogoAnimationBronze;
            default:
              return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
          }
        }
        case 'direction': {
          switch (planType) {
            case 'gold':
              return isDark
                ? goldDarkAnimatedMapping[directionId as keyof typeof goldDarkAnimatedMapping]
                : goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping];
            case 'silver':
              return isDark
                ? silverDarkAnimatedMapping[directionId as keyof typeof silverDarkAnimatedMapping]
                : silverAnimatedMapping[directionId as keyof typeof silverAnimatedMapping];
            case 'bronze':
              return isDark
                ? bronzeDarkAnimatedMapping[directionId as keyof typeof bronzeDarkAnimatedMapping]
                : bronzeAnimatedMapping[directionId as keyof typeof bronzeAnimatedMapping];
            default:
              return null;
          }
        }
        default:
          break;
      }
    }

    // For bundle courses, use tier-specific BOGO animations (gradient theme)
    if (isBundleCourse()) {
      switch (planType) {
        case 'gold':
          return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
        case 'silver':
          return isDark ? bogoAnimationSilverDark : bogoAnimationSilver;
        case 'bronze':
          return isDark ? bogoAnimationBronzeDark : bogoAnimationBronze;
        default:
          return isDark ? bogoAnimationGoldDark : bogoAnimationGold;
      }
    }

    // Default behavior: use direction-based animations
    switch (planType) {
      case 'gold':
        return isDark
          ? goldDarkAnimatedMapping[directionId as keyof typeof goldDarkAnimatedMapping]
          : goldAnimatedMapping[directionId as keyof typeof goldAnimatedMapping];
      case 'silver':
        return isDark
          ? silverDarkAnimatedMapping[directionId as keyof typeof silverDarkAnimatedMapping]
          : silverAnimatedMapping[directionId as keyof typeof silverAnimatedMapping];
      case 'bronze':
        return isDark
          ? bronzeDarkAnimatedMapping[directionId as keyof typeof bronzeDarkAnimatedMapping]
          : bronzeAnimatedMapping[directionId as keyof typeof bronzeAnimatedMapping];
      default:
        return null;
    }
  };

  // Function to get color scheme with course-specific overrides
  const getMainColor = () => {
    const override = typeof courseAny?.upsellPrimaryColor === 'string' ? courseAny.upsellPrimaryColor.trim() : '';
    if (override) return override;

    // For bundle courses, use gradient theme from BundleUpgradeOptionsModal
    if (isBundleCourse()) {
      return 'linear-gradient(to right, #ED5A26, #1CBF73)'; // Gradient for bundles
    }

    // Default behavior: use original mainColor
    return mainColor;
  };

  const isGoldPurchase = currentPayment?.type === PaymentDtoTypeEnum.Purchase;
  const hasSubscription = currentPayment?.type === PaymentDtoTypeEnum.Subscription;

  useEffect(() => {
    dispatch(actions.courses.setPaymentOption(null));
  }, []);

  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!directionId) return <></>;

  const plans: Array<Plan> = [
    {
      priceMonthly: monthlyPrices?.gold,
      priceAnnual: annualPrices?.gold,
      purchasePrice: purchasePrice?.gold,
      installmentPrice: installmentPrice?.gold,
      type: SubscribeDtoTypeEnum.Gold,
      capabilities: goldCapabilities,
      capabilitiesResponsive: goldCapabilitiesResponsive,
      animationData: getAnimationData('gold', false),
      darkAnimationData: getAnimationData('gold', true),
      animationKey: getPlanAnimationKey('gold'),
    },

    {
      priceMonthly: monthlyPrices?.silver,
      priceAnnual: annualPrices?.silver,
      purchasePrice: isStudyMaterialCourse ? undefined : purchasePrice?.silver,
      installmentPrice: null, // Disabled - show N/A instead
      type: SubscribeDtoTypeEnum.Silver,
      capabilities: silverCapabilities,
      capabilitiesResponsive: silverCapabilitiesResponsive,
      animationData: getAnimationData('silver', false),
      darkAnimationData: getAnimationData('silver', true),
      animationKey: getPlanAnimationKey('silver'),
    },

    {
      priceMonthly: monthlyPrices?.bronze,
      priceAnnual: annualPrices?.bronze,
      purchasePrice: isStudyMaterialCourse ? undefined : purchasePrice?.bronze,
      installmentPrice: null, // Disabled - show N/A instead
      type: SubscribeDtoTypeEnum.Bronze,
      capabilities: bronzeCapabilities,
      capabilitiesResponsive: bronzeCapabilitiesResponsive,
      animationData: getAnimationData('bronze', false),
      darkAnimationData: getAnimationData('bronze', true),
      animationKey: getPlanAnimationKey('bronze'),
    },
  ];

  const getPlan = (type: string) => plans.find((plan) => plan.type === type);

  const goldPlan = getPlan(SubscribeDtoTypeEnum.Gold);
  // const silverPlan = getPlan(SubscribeDtoTypeEnum.Silver);
  // const bronzePlan = getPlan(SubscribeDtoTypeEnum.Bronze);

  const countPrices = (plan: Plan | undefined) => {
    if (!plan) return;

    return (
      (plan.priceAnnual ? 1 : 0) +
      (plan.priceMonthly ? 1 : 0) +
      (plan.installmentPrice ? 1 : 0) +
      (plan.type === SubscribeDtoTypeEnum.Gold && plan.purchasePrice !== undefined ? 1 : 0)
    );
  };

  // const goldCounts = countPrices(goldPlan);

  const counts = {
    // goldCount: goldCounts,
    // silverCount: silverCounts,
    // bronzeCount: bronzeCounts,
  };

  // Filter out Bronze for new users on certification courses
  const isCertificate = courseType === 'certificate' || courseType === CourseTypeEnum.Certificate;
  const isNewUserForBronze =
    (currentPayment?.level as string) !== 'bronze' && (currentPayment?.level as any) !== SubscribeDtoTypeEnum.Bronze;
  const plansToRender = plans.filter((plan) => {
    if (isCertificate && isNewUserForBronze && plan.type === SubscribeDtoTypeEnum.Bronze) {
      return false;
    }
    return true;
  });

  // Calculate total plans count based on available pricing
  const countPlans = plansToRender.reduce((count, plan) => {
    const hasAnyPrice = plan.priceMonthly || plan.priceAnnual || plan.purchasePrice || plan.installmentPrice;
    return hasAnyPrice ? count + 1 : count;
  }, 0);

  const getCapabilitiesMargin = () => {
    let actualPlanPricesMargin = 0;

    plans.forEach(() => {
      const countPlanPrices = countPrices(goldPlan);

      if (countPlanPrices === 1) {
        actualPlanPricesMargin = 416;
      }

      if (countPlanPrices === 2) {
        actualPlanPricesMargin = 473;
      }

      if (countPlanPrices === 3) {
        actualPlanPricesMargin = 526;
      }
    });

    return `${actualPlanPricesMargin}px`;
  };

  return (
    <Root style={{ marginTop: isResponsive ? '-557px' : '15px' }}>
      <Wrapper>
        <Container $isShowPurchase={false} isResponsive={isResponsive} isCentered={isStudyMaterialCourse}>
          {!isResponsive && (
            <div
              style={{
                width: '70px',
                height: '70px',
                position: 'absolute',
                top: '260px',
                left: shouldCenterLegacy ? '50%' : '100px',
                transform: shouldCenterLegacy ? 'translateX(-50%)' : undefined,
              }}></div>
          )}

          {!shouldCenterLegacy && (
            <>
              <ImageDivArrowRight>
                {pointerUrl ? (
                  pointerUrlStatus === 'loaded' && pointerUrlData ? (
                    <Lottie
                      key={pointerLottieKey}
                      animationData={pointerUrlData}
                      style={{ width: '70px', height: '70px' }}
                    />
                  ) : pointerUrlStatus === 'error' ? (
                    <img src={arrowDow2} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
                  ) : (
                    <div style={{ width: '70px', height: '70px' }} />
                  )
                ) : (
                  <img src={arrowDow2} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
                )}
              </ImageDivArrowRight>

              <ImageDivArrowLeft>
                {pointerUrl ? (
                  pointerUrlStatus === 'loaded' && pointerUrlData ? (
                    <Lottie
                      key={pointerLottieKey}
                      animationData={pointerUrlData}
                      style={{ width: '70px', height: '70px', transform: 'scaleX(-1)' }}
                    />
                  ) : pointerUrlStatus === 'error' ? (
                    <img src={arrowDown} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
                  ) : (
                    <div style={{ width: '70px', height: '70px' }} />
                  )
                ) : (
                  <img src={arrowDown} alt="arrow" style={{ filter: isDarkMode ? 'invert(1)' : 'unset' }} />
                )}
              </ImageDivArrowLeft>
            </>
          )}

          {!isResponsive && currentPayment?.level !== 'gold' && (
            <BonusSection
              isProduct={true}
              isStudyMaterial={isStudyMaterialCourse}
              setIsShowBonusModal={setIsShowBonusModal}
              course={currentCourse}
            />
          )}
          {plans && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: getCapabilitiesMargin(),
              }}>
              {!isResponsive &&
                commonCapabilities.map((cap, index) => (
                  <div
                    style={{
                      alignItems: 'center',
                      width: '230px',
                      textAlign: 'left',
                    }}
                    key={index}>
                    <CapabilitiesContainer>{cap.title}</CapabilitiesContainer>
                  </div>
                ))}
            </div>
          )}
          {plansToRender.map((plan, index) => {
            const termsCount =
              (plan.priceAnnual ? 1 : 0) +
              (plan.priceMonthly ? 1 : 0) +
              (plan.purchasePrice ? 1 : 0) +
              (plan.installmentPrice ? 1 : 0);

            const isLowest = currentPayment?.level
              ? levelGradeMapping[currentPayment.level] > levelGradeMapping[plan.type]
              : false;

            return (
              <SubscriptionItem
                key={index}
                isResponsive={isResponsive}
                plan={plan}
                termsCount={termsCount}
                isStudyMaterial={isStudyMaterialCourse}
                setIsShowBonusModal={setIsShowBonusModal}
                indexPlan={index}
                counts={counts}
                animationData={isDarkMode ? plan.darkAnimationData : plan.animationData}
                animationKey={plan.animationKey}
                setPlanType={setPlanType}
                planType={planType}
                mainColor={getMainColor()}
                countPlans={countPlans}
                isMaxHeight={false}
                isShow={activeIndex === index}
                activePeriod={activePeriod}
                setActivePeriod={setActivePeriod}
                setActiveIndex={setActiveIndex}
                cancelSubscriptionDowngrade={cancelSubscriptionDowngrade}
                toggleSubscription={toggleSubscription}
                isLowest={isLowest}
                courseId={courseId}
                isCurrentSubscription={currentPayment?.level === plan.type}
                isSubscriptionUpdating={isSubscriptionUpdating}
                currentPayment={currentPayment}
                hasSubscriptionDowngrade={Boolean(nextPayment)}
                nextPayment={nextPayment?.level === plan.type ? nextPayment : undefined}
                isShowButton={!isGoldPurchase}
                hasSubscription={hasSubscription}
                mostEffectiveLabel={mostEffectiveLabel}
                isBundle={_isBundle}
              />
            );
          })}
        </Container>

        {isTrialText && (
          <TrialText
            isDowngrade={nextPayment?.type === 'Free'}
            cancelSubscriptionDowngrade={() => cancelSubscriptionDowngrade(courseId, true)}
            currentPayment={currentPayment}
            startTrial={() => startTrial(courseId)}
            isTrialStarting={isTrialStarting}
            startUnsubscribeProcess={startUnsubscribeProcess}
          />
        )}
      </Wrapper>
    </Root>
  );
};

export default Products;

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  grid-gap: 32px;

  display: flex;
  flex-direction: column;
  justify-content: start;
`;

const ImageDivArrowRight = styled.div`
  width: 70px;
  height: 70px;
  position: absolute;
  top: 40px;
  right: 185px;
  z-index: 1;

  @media (max-width: 1180px) {
    top: 190px;
    right: 30px;
  }
`;

const ImageDivArrowLeft = styled.div`
  width: 70px;
  height: 70px;
  position: absolute;
  top: 40px;
  left: 185px;
  z-index: 1;

  @media (max-width: 1180px) {
    top: 140px;
    left: 90px;
  }

  @media (max-width: 570px) {
    top: 130px;
    left: 30px;
  }
`;

const CapabilitiesContainer = styled.div`
  ${({ theme: { typography } }) => typography.body_large_semibold_16}
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  margin-bottom: 21px;
`;

const Container = styled.div<{ $isShowPurchase: boolean; isResponsive: boolean; isCentered?: boolean }>`
  display: ${({ isResponsive }) => (isResponsive ? 'unset' : 'flex')};
  height: max-content;
  gap: 20px;

  ${({ isCentered }) =>
    isCentered &&
    css`
      margin-left: auto;
      margin-right: auto;
    `}

  ${({ $isShowPurchase }) =>
    $isShowPurchase
      ? css`
          margin: 0 auto auto;
          display: grid;
          grid-template-columns: 352px;
        `
      : css``}

  @media (max-width: 1180px) {
    width: 500px;
  }

  @media (max-width: 570px) {
    width: 370px;
  }

  @media (max-width: 370px) {
    width: 320px;
  }
`;
